import { SystemNumberStatusEnum } from '@shared/enums/system-number-status-enum';
import { ISearchTcrCampaignsRecord } from '@shared/models';
import { IClientLocalSystemNumberRecord } from '../../../../hooks/useClientLocalSystemNumbers';

export const normalizePhoneDigits = (phoneNumber: number): string => {
  const digits = phoneNumber?.toString() ?? '';
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

export const getAreaCode = (phoneNumber: number): string => normalizePhoneDigits(phoneNumber).slice(0, 3);

export const uniqueSorted = (values: (string | null | undefined)[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out.sort((a, b) => a.localeCompare(b));
};

export const tcrCampaignIdsForProviders = (
  records: IClientLocalSystemNumberRecord[],
  selectedProviderNames: string[],
): string[] => {
  if (selectedProviderNames.length === 0) {
    return uniqueSorted(records.map(r => r.tcrCampaignId));
  }
  const providerSet = new Set(selectedProviderNames);
  return uniqueSorted(
    records
      .filter(r => !!r.providerName && providerSet.has(r.providerName))
      .map(r => r.tcrCampaignId),
  );
};

export const tcrCampaignLabel = (
  tcrCampaignId: string,
  records: IClientLocalSystemNumberRecord[],
  selectedProviderNames: string[],
): string => {
  const providerSet = selectedProviderNames.length > 0 ? new Set(selectedProviderNames) : null;
  const matchingRecords = records.filter(
    r => r.tcrCampaignId === tcrCampaignId
      && !!r.providerName
      && (!providerSet || providerSet.has(r.providerName!)),
  );
  const providers = uniqueSorted(matchingRecords.map(r => r.providerName!));
  const messageType = matchingRecords.find(r => !!r.messageType)?.messageType ?? null;
  if (providers.length === 0) return tcrCampaignId;
  const label = `${tcrCampaignId} - ${providers.join(', ')}`;
  return messageType ? `${label} - ${messageType}` : label;
};

export const getActiveNumberCount = (
  records: IClientLocalSystemNumberRecord[],
  tcrCampaignId: string,
): number => {
  return records.filter(
    r =>
      r.tcrCampaignId === tcrCampaignId &&
      r.status !== SystemNumberStatusEnum.DEACTIVATED,
  ).length;
};

export const getValidQuantity = (activeCount: number, desired: number): number => {
  const max = Math.max(49 - activeCount, 0);
  return Math.min(desired, max);
};

// Resolved, search-ready metadata for a single selected TCR campaign.
export interface ICampaignSearchContext {
  tcrCampaignId: string;
  grTcrCampaignId: string | null;
  providerName: string;
  activeNumberCount: number;
  availableSlots: number; // Math.max(49 - activeNumberCount, 0)
}

// Maps a tcrCampaignId to its GR id, provider, and 49-cap slot count.
// providerName falls back to tcrCampaignId when unresolved (handleSearch treats that as an error).
export const resolveCampaignContext = (
  tcrCampaignId: string,
  records: IClientLocalSystemNumberRecord[],
  allTcrCampaigns: ISearchTcrCampaignsRecord[],
): ICampaignSearchContext => {
  const matchingTcrCampaign = allTcrCampaigns.find(c => c.tcrCampaignId === tcrCampaignId);
  const matchingRecord = records.find(r => r.tcrCampaignId === tcrCampaignId);
  const grTcrCampaignId = matchingRecord?.grTcrCampaignId ?? matchingTcrCampaign?.id ?? null;
  const providerName = matchingRecord?.providerName ?? matchingTcrCampaign?.provider?.name ?? tcrCampaignId;
  const activeNumberCount = getActiveNumberCount(records, tcrCampaignId);
  const availableSlots = Math.max(49 - activeNumberCount, 0);

  return { tcrCampaignId, grTcrCampaignId, providerName, activeNumberCount, availableSlots };
};

// Groups campaigns by provider — the dimension along which numbers collide, so a single
// search must cover one provider at a time. Insertion order preserved per provider.
export const groupCampaignsByProvider = (
  contexts: ICampaignSearchContext[],
): Map<string, ICampaignSearchContext[]> =>
  contexts.reduce((groups, context) => {
    const existing = groups.get(context.providerName) ?? [];
    return new Map(groups).set(context.providerName, [...existing, context]);
  }, new Map<string, ICampaignSearchContext[]>());

// Allocates a provider's number pool across its campaigns as disjoint slices: each takes a
// non-overlapping window of width min(requestedQuantity, availableSlots), and later campaigns
// get [] once the pool runs out. The Set is a defensive dedupe (GR already returns distinct).
export const allocateNumbersSequentially = (
  numbers: string[],
  campaigns: ICampaignSearchContext[],
  requestedQuantity: number,
): { tcrCampaignId: string; phoneNumbers: string[] }[] => {
  const distinct = Array.from(new Set(numbers));

  return campaigns.reduce<{ cursor: number; out: { tcrCampaignId: string; phoneNumbers: string[] }[] }>(
    ({ cursor, out }, campaign) => {
      const cap = Math.min(requestedQuantity, campaign.availableSlots);
      const phoneNumbers = distinct.slice(cursor, cursor + cap);
      return {
        cursor: cursor + phoneNumbers.length,
        out: [...out, { tcrCampaignId: campaign.tcrCampaignId, phoneNumbers }],
      };
    },
    { cursor: 0, out: [] },
  ).out;
};

// Per-campaign search/purchase outcome row. PurchaseNumbersModal re-exports this for its
// existing consumers (kept here so the row-building helpers below avoid a circular import).
export interface IPurchaseProviderResult {
  providerName: string;
  tcrCampaignId: string;
  grTcrCampaignId: string;
  phoneNumbers: string[];
  activeNumberCount: number;
  availableSlots: number;
  error: string | null;
  purchaseError?: string | null;
}

// Builds a per-campaign error string. Pure: depends only on its arguments.
export const buildErrorMessage = (
  errorType: 'areaCode' | 'capacity' | 'quantity',
  params: { providerName?: string; activeNumberCount?: number; tcrCampaignId?: string; available?: number; requested?: number; areaCode?: string },
): string => {
  switch (errorType) {
    case 'areaCode':
      return `Area code is not available for ${params.providerName}.`;
    case 'capacity':
      return `There are currently ${params.activeNumberCount}/49 numbers on ${params.tcrCampaignId}. Cannot order ${params.requested} numbers.`;
    case 'quantity':
      // Name the searched area code when known — with replacement disallowed (MES-2595), a
      // short/zero result means that specific NPA lacks inventory. Fall back to the legacy
      // wording when absent so other callers/tests keep their current messages.
      if (params.available === 0) {
        return params.areaCode
          ? `No numbers are available on ${params.tcrCampaignId} for ${params.providerName} in area code ${params.areaCode}.`
          : `No numbers are available on ${params.tcrCampaignId} for ${params.providerName} in this area code.`;
      }
      return params.areaCode
        ? `Only ${params.available} out of ${params.requested} numbers available on ${params.tcrCampaignId} for ${params.providerName} in area code ${params.areaCode}.`
        : `Only ${params.available} numbers out of ${params.requested} available on ${params.tcrCampaignId} for ${params.providerName}.`;
  }
};

// Builds a result row from a resolved campaign context plus per-row outcome fields.
export const buildRow = (
  context: ICampaignSearchContext,
  phoneNumbers: string[],
  error: string | null,
): IPurchaseProviderResult => ({
  providerName: context.providerName,
  tcrCampaignId: context.tcrCampaignId,
  grTcrCampaignId: context.grTcrCampaignId ?? '',
  phoneNumbers,
  activeNumberCount: context.activeNumberCount,
  availableSlots: context.availableSlots,
  error,
});

// Warns whenever a campaign gets fewer numbers than requested, distinguishing the cause.
// Availability shortfall takes PRIORITY over capacity: a search that returns zero or fewer
// numbers than the slot cap could hold tells ops the inventory isn't there, and that signal
// must surface even when the campaign is also slot-capped. Capacity is the secondary case —
// it only fires when the pool delivered the full slot cap, as a heads-up that ops is buying a
// subset of what they requested (a successful fill to the campaign's real 49-hold maximum).
export const classifyAllocatedRow = (context: ICampaignSearchContext, phoneNumbers: string[], requestedQuantity: number, areaCode?: string): IPurchaseProviderResult => {
  // Pool shortfall: fewer numbers came back than the slice cap (min(requested, slots)) allowed.
  // Wins over capacity so a zero/short search is always reported as an availability problem,
  // including when the campaign is also slot-capped.
  if (phoneNumbers.length < Math.min(requestedQuantity, context.availableSlots)) {
    return buildRow(context, phoneNumbers, buildErrorMessage('quantity', { available: phoneNumbers.length, requested: requestedQuantity, tcrCampaignId: context.tcrCampaignId, providerName: context.providerName, areaCode }));
  }
  // Capacity-limited: slot cap below the ask, but the pool filled it completely. No availability
  // problem — flag that ops is buying a subset (the campaign's real max under the 49-hold cap).
  if (context.availableSlots < requestedQuantity) {
    return buildRow(context, phoneNumbers, buildErrorMessage('capacity', { activeNumberCount: context.activeNumberCount, tcrCampaignId: context.tcrCampaignId, requested: requestedQuantity }));
  }
  return buildRow(context, phoneNumbers, null);
};

// Maps a rejected per-provider search to one error row per campaign in that group,
// preserving the area-code vs generic classification (and the 1:1 campaign->row invariant).
export const buildProviderFailureRows = (group: ICampaignSearchContext[], reason: unknown): IPurchaseProviderResult[] => {
  const err = reason as { response?: { data?: { message?: string } }; message?: string } | undefined;
  const rawMessage: string = err?.response?.data?.message ?? err?.message ?? '';
  const isAreaCodeError = /area code.*not available/i.test(rawMessage);
  return group.map(context => {
    const message = isAreaCodeError
      ? buildErrorMessage('areaCode', { providerName: context.providerName })
      : rawMessage || `An unexpected error occurred while searching for numbers on ${context.tcrCampaignId}. Please try again.`;
    return buildRow(context, [], message);
  });
};
